import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from '../redux/hooks/useSelector'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { applyThemeTokens, ThemeMode } from '../styles/tokens'
import { rebuildNewThemeObject } from '../newTheme'
import { rebuildMuiTheme } from '../muiTheme'
import { APEX } from '../constants/businessTerritory'

// Výchozí vzhled podle teritoria: Apex = tmavá navy (midnight, #0A1E3C
// z loga), Imperium a ostatní = výchozí světlejší navy (#11457F).
function territoryDefaultMode(businessTerritoryId?: number): ThemeMode {
  return businessTerritoryId === APEX ? 'midnight' : 'default'
}

// Pořadí, ve kterém ikona paletky prochází vzhledy.
const MODE_ORDER: ThemeMode[] = ['default', 'luxury', 'dollar', 'midnight']

// Popisek tlačítka (TopBar.tsx, User.tsx) ukazuje vzhled, na který se
// přepne dalším kliknutím.
export const nextThemeModeLabelKey: Record<ThemeMode, string> = {
  default: 'SWITCH_TO_LUXURY_THEME',
  luxury: 'SWITCH_TO_DOLLAR_THEME',
  dollar: 'SWITCH_TO_MIDNIGHT_THEME',
  midnight: 'SWITCH_TO_DEFAULT_THEME',
}

// Přepne aktivní barevnou paletu (tokens.ts) a znovu sestaví objekty, které
// z ní čerpají (newTheme.ts, muiTheme.ts), aby se změna promítla i tam, kde
// se čte přímo ze statického importu, ne přes MUI theme.
function applyMode(mode: ThemeMode) {
  applyThemeTokens(mode)
  rebuildNewThemeObject()
  return rebuildMuiTheme()
}

interface ThemeModeContextValue {
  mode: ThemeMode
  toggleThemeMode: () => void
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'default',
  toggleThemeMode: () => {},
})

export const useThemeMode = () => useContext(ThemeModeContext)

export const ThemeModeProvider: React.FC = ({ children }) => {
  // Zdroj pravdy pro barvu je teritorium přihlášeného uživatele (redux).
  const businessTerritoryId = useSelector(state => state.auth.user?.businessTerritoryId)
  const territoryMode = territoryDefaultMode(businessTerritoryId)

  const [mode, setMode] = useState<ThemeMode>(territoryMode)
  const [muiTheme, setMuiTheme] = useState(() => applyMode(territoryMode))

  // Po přihlášení i po přepnutí teritoria (změna user.businessTerritoryId
  // v reduxu) nastav barvu na výchozí vzhled daného teritoria; přebije tím
  // případný ruční výběr z paletky (ten je jen dočasný, session-only).
  useEffect(() => {
    setMode(territoryMode)
    setMuiTheme(applyMode(territoryMode))
  }, [territoryMode])

  const value = useMemo<ThemeModeContextValue>(
    () => ({
      mode,
      // Ruční override přes paletku. Nepersistuje se — po reloadu nebo
      // změně teritoria se vrátí výchozí vzhled dle teritoria.
      toggleThemeMode: () => {
        setMode(prevMode => {
          const nextMode = MODE_ORDER[(MODE_ORDER.indexOf(prevMode) + 1) % MODE_ORDER.length]
          setMuiTheme(applyMode(nextMode))
          return nextMode
        })
      },
    }),
    [mode]
  )

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}
